/**
 * AdminQuotesCard — CMS for the rotating quotes shown on /login,
 * /register, and /for-brokers.
 *
 * Three decks: login (17 seed), signup (10 seed), institutional (11 seed).
 * Backed by the `platform_quotes` Mongo collection. Auto-seeds on first
 * load from the historical hardcoded arrays.
 *
 * Actions: add · inline edit (text/author) · toggle active · delete ·
 * emergency reseed.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Plus, Save, Trash2, RefreshCw, Quote as QuoteIcon,
    Eye, EyeOff, RotateCcw,
} from "lucide-react";

const DECKS = [
    { key: "login",         label: "Login screen",       hint: "Shown under the hero on /login." },
    { key: "signup",        label: "Signup screen",      hint: "Beginner-friendly deck on /register." },
    { key: "institutional", label: "Institutional page", hint: "Renders on /for-brokers for CMA + MoE decision-makers." },
];

export default function AdminQuotesCard() {
    const [activeDeck, setActiveDeck] = useState("login");
    const [rows,       setRows]       = useState([]);
    const [loading,    setLoading]    = useState(false);
    const [error,      setError]      = useState(null);
    const [draft,      setDraft]      = useState({ text: "", author: "" });
    const [creating,   setCreating]   = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            // Fetch ALL decks so the sub-tab count badges (n) are accurate
            // for every deck, not just the currently-selected one.
            const { data } = await api.get("/admin/quotes");
            setRows(data.quotes || []);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => rows.filter((r) => r.deck === activeDeck), [rows, activeDeck]);
    const deckMeta = DECKS.find((d) => d.key === activeDeck);

    const onCreate = async () => {
        if (!draft.text.trim() || !draft.author.trim()) {
            toast.error("Both text and author are required.");
            return;
        }
        setCreating(true);
        try {
            await api.post("/admin/quotes", {
                deck:   activeDeck,
                text:   draft.text.trim(),
                author: draft.author.trim(),
                active: true,
            });
            toast.success("Quote added.");
            setDraft({ text: "", author: "" });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to add quote.");
        } finally {
            setCreating(false);
        }
    };

    const onReseed = async () => {
        if (!window.confirm(
            "Emergency reseed will DELETE all quotes across all 3 decks and reinsert the historical catalog. Continue?",
        )) return;
        try {
            const { data } = await api.post("/admin/quotes/reseed", {});
            toast.success(`Reseeded — ${data.total} quotes across 3 decks.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reseed failed.");
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-quotes-card">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-primary mb-1">
                        Quotes CMS
                    </div>
                    <h2 className="text-xl font-extrabold tracking-tight">Rotating quotes across auth pages</h2>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Manage what appears under the hero on <code className="text-[11px]">/login</code>,{" "}
                        <code className="text-[11px]">/register</code>, and{" "}
                        <code className="text-[11px]">/for-brokers</code>. Changes are picked up on the next page load.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        disabled={loading}
                        data-testid="quotes-refresh"
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-border bg-card text-sm font-semibold hover:bg-muted/50 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                    <button
                        onClick={onReseed}
                        data-testid="quotes-reseed"
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-destructive/40 text-destructive text-sm font-semibold hover:bg-destructive/10"
                        title="Wipe all decks and reinsert the historical catalog"
                    >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Reseed defaults
                    </button>
                </div>
            </div>

            {/* Deck tabs */}
            <div className="flex flex-wrap gap-1 border-b border-border">
                {DECKS.map((d) => (
                    <button
                        key={d.key}
                        onClick={() => setActiveDeck(d.key)}
                        data-testid={`quotes-deck-${d.key}`}
                        className={
                            "px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors " +
                            (activeDeck === d.key
                                ? "border-primary text-primary"
                                : "border-transparent text-muted-foreground hover:text-foreground")
                        }
                    >
                        {d.label}
                        <span className="ml-1.5 text-[10px] text-muted-foreground font-mono">
                            ({rows.filter((r) => r.deck === d.key).length || 0})
                        </span>
                    </button>
                ))}
            </div>

            {deckMeta && (
                <div className="text-xs text-muted-foreground">
                    <QuoteIcon className="w-3 h-3 inline mr-1.5 opacity-60" />
                    {deckMeta.hint}
                </div>
            )}

            {/* Add new */}
            <div className="rounded-lg border border-border bg-card p-3" data-testid="quotes-add-form">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-2">
                    Add a quote to <b className="text-primary">{deckMeta?.label}</b>
                </div>
                <div className="grid md:grid-cols-[1fr_240px_auto] gap-2">
                    <textarea
                        value={draft.text}
                        onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                        placeholder="Quote text…"
                        rows={2}
                        maxLength={800}
                        data-testid="quotes-new-text"
                        className="px-3 py-2 rounded border border-border bg-background text-sm outline-none focus:border-primary resize-none"
                    />
                    <input
                        value={draft.author}
                        onChange={(e) => setDraft({ ...draft, author: e.target.value })}
                        placeholder="Author (e.g. Warren Buffett)"
                        maxLength={120}
                        data-testid="quotes-new-author"
                        className="px-3 py-2 rounded border border-border bg-background text-sm outline-none focus:border-primary"
                    />
                    <button
                        onClick={onCreate}
                        disabled={creating}
                        data-testid="quotes-new-submit"
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground font-bold text-sm hover:brightness-110 disabled:opacity-50"
                    >
                        {creating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Add
                    </button>
                </div>
            </div>

            {error && (
                <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                    {error}
                </div>
            )}

            {/* Rows */}
            <div className="space-y-2" data-testid="quotes-list">
                {loading && filtered.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-6">Loading…</div>
                )}
                {!loading && filtered.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-6">
                        No quotes yet. Add one above or click <b>Reseed defaults</b>.
                    </div>
                )}
                {filtered.map((q) => (
                    <QuoteRow key={q.id} row={q} onReload={load} />
                ))}
            </div>
        </div>
    );
}


function QuoteRow({ row, onReload }) {
    const [text,    setText]    = useState(row.text);
    const [author,  setAuthor]  = useState(row.author);
    const [saving,  setSaving]  = useState(false);

    const dirty = text !== row.text || author !== row.author;

    const save = async () => {
        if (!dirty) return;
        if (!text.trim() || !author.trim()) {
            toast.error("Both fields are required.");
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/admin/quotes/${row.id}`, {
                text:   text.trim(),
                author: author.trim(),
            });
            toast.success("Saved.");
            await onReload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async () => {
        try {
            await api.patch(`/admin/quotes/${row.id}`, { active: !row.active });
            toast.success(row.active ? "Hidden from carousel." : "Made visible.");
            await onReload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Toggle failed.");
        }
    };

    const del = async () => {
        if (!window.confirm(`Delete this quote by "${row.author}"?`)) return;
        try {
            await api.delete(`/admin/quotes/${row.id}`);
            toast.success("Deleted.");
            await onReload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed.");
        }
    };

    return (
        <div
            className={
                "rounded-lg border p-3 grid md:grid-cols-[1fr_220px_auto] gap-2 items-start transition-opacity " +
                (row.active ? "border-border bg-card" : "border-border/60 bg-muted/30 opacity-70")
            }
            data-testid={`quotes-row-${row.id}`}
        >
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={2}
                maxLength={800}
                className="px-2 py-1.5 rounded border border-border bg-background text-sm outline-none focus:border-primary resize-none"
            />
            <input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                maxLength={120}
                className="px-2 py-1.5 rounded border border-border bg-background text-sm outline-none focus:border-primary"
            />
            <div className="flex items-center gap-1 justify-end">
                <button
                    onClick={save}
                    disabled={!dirty || saving}
                    title="Save changes"
                    data-testid={`quotes-save-${row.id}`}
                    className="p-2 rounded border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                </button>
                <button
                    onClick={toggleActive}
                    title={row.active ? "Hide from carousel" : "Show in carousel"}
                    data-testid={`quotes-toggle-${row.id}`}
                    className="p-2 rounded border border-border hover:bg-muted"
                >
                    {row.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button
                    onClick={del}
                    title="Delete permanently"
                    data-testid={`quotes-delete-${row.id}`}
                    className="p-2 rounded border border-destructive/40 text-destructive hover:bg-destructive/10"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
